import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import theme from './theme';
import Layout from './components/Layout/Layout';
import Home from './pages/Home';
import Login from './pages/Login';
import CreateAccount from './pages/CreateAccount';
import TestAPI from './pages/TestAPI';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import LessonDetail from './pages/LessonDetail';
import MyCourses from './pages/MyCourses';
import Purchases from './pages/Purchases';
import Profile from './pages/Profile';
import Mentors from './pages/Mentors';
import About from './pages/About';
import ExamDetail from './pages/ExamDetail';
import TakeExam from './pages/TakeExam';
import ExamResult from './pages/ExamResult';
import StandaloneExams from './pages/StandaloneExams';
import StandaloneExamDetail from './pages/StandaloneExamDetail';
import TakeStandaloneExam from './pages/TakeStandaloneExam';
import StandaloneExamResult from './pages/StandaloneExamResult';
import CertificateVerify from './pages/CertificateVerify';


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Layout wrapper component
const LayoutWrapper = () => {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/create-account" element={<CreateAccount />} />
              <Route path="/test-api" element={<TestAPI />} />
              <Route path="/" element={<LayoutWrapper />}>
                {/* Django-style links use a trailing slash; match both so the outlet is not blank in production */}
                <Route path="verify/:token" element={<CertificateVerify />} />
                <Route path="verify/:token/" element={<CertificateVerify />} />
                <Route index element={<Home />} />
                <Route path="courses" element={<Courses />} />
                <Route path="courses/:id" element={<CourseDetail />} />
                <Route path="courses/:courseId/lessons/:lessonId" element={<LessonDetail />} />
                <Route path="mentors" element={<Mentors />} />
                <Route path="my-courses" element={<MyCourses />} />
                <Route path="purchases" element={<Purchases />} />
                <Route path="profile" element={<Profile />} />
                <Route path="about" element={<About />} />
                
                {/* Standalone Exam Routes */}
                <Route path="exams" element={<StandaloneExams />} />
                <Route path="exams/:hashed" element={<StandaloneExamDetail />} />
                <Route path="exams/:hashed/take" element={<TakeStandaloneExam />} />
                <Route path="exams/:hashed/result/:attemptId" element={<StandaloneExamResult />} />
                
                {/* Course Exam Routes */}
                <Route path="courses/:courseId/exams/:examId" element={<ExamDetail />} />
                <Route path="courses/:courseId/exams/:examId/take" element={<TakeExam />} />
                <Route path="courses/:courseId/exams/:examId/result/:attemptId" element={<ExamResult />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1e293b',
              color: '#f8fafc',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            },
          }}
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;